import { useEffect, useMemo, useRef } from "react";
import dayjs from "dayjs";
import { Button, Drawer, Heading, Pill, SkeletonBase, Text } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import { useLayoutStore } from "@context/useLayoutStore";
import type { Interval } from "@models/ISchema";
import type { AnnualOccurrence, AnnualScheduleGroup } from "../../adapters";
import type { AnnualScheduleCellSelection, AnnualScheduleContext } from "../../types";
import { usePackageAnnualCell } from "../../hooks/use-package-annual-cell";
import SessionOccurrenceDetails from "../session-occurrence-details";
import { useAnnualDrawerLifecycle } from "./hooks/use-annual-drawer-lifecycle";
import styles from "./annualScheduleDrawer.module.scss";

interface Props {
  /** Keeps the Avocado Drawer mounted; opens it only with complete cell details. */
  isOpen: boolean;
  context: AnnualScheduleContext;
  scopeKey: string;
  selection?: AnnualScheduleCellSelection | null;
  /** A MatrixTable group representing one league (`Link`), whose code is `Link.code`. */
  group?: AnnualScheduleGroup;
  week?: Interval;
  onClose: () => void;
  /** Refreshes the summary when a newly fetched detail response has no occurrences. */
  onEmpty: () => void;
}

/**
 * Formats the selected week for the drawer header.
 *
 * @param week - Catalog week with its starting and ending dates.
 * @returns A compact range such as `15-21/09/26`; includes both months or years when
 * they differ, or an empty string when either date is unavailable or invalid.
 */
export const formatAnnualWeekDateRange = (week: Interval): string => {
  if (!week.startingDate || !week.endingDate) return "";

  const start = dayjs(week.startingDate.slice(0, 10));
  const end = dayjs(week.endingDate.slice(0, 10));
  if (!start.isValid() || !end.isValid()) return "";

  if (start.isSame(end, "month")) return `${start.format("DD")}-${end.format("DD/MM/YY")}`;
  if (start.isSame(end, "year")) return `${start.format("DD/MM")}-${end.format("DD/MM/YY")}`;
  return `${start.format("DD/MM/YY")}-${end.format("DD/MM/YY")}`;
};

/** Groups chronological occurrences by date while preserving their order within each day. */
const groupOccurrencesByDate = (occurrences: AnnualOccurrence[]) => {
  const grouped = new Map<string, AnnualOccurrence[]>();

  occurrences.forEach(occurrence => {
    const day = grouped.get(occurrence.date) ?? [];
    day.push(occurrence);
    grouped.set(occurrence.date, day);
  });

  return [...grouped.entries()];
};

/** Formats a date at local noon so time-zone conversion cannot change its calendar day. */
const formatOccurrenceDate = (date: string, language: string) =>
  new Intl.DateTimeFormat(language, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(`${date}T12:00:00`));

/**
 * Displays one league component's sessions for the selected week.
 *
 * The Avocado Drawer stays mounted so its entrance and exit animations can run.
 * Incomplete selections leave it closed, while the lifecycle hook retains the last
 * complete selection until the closing animation finishes.
 */
export default function AnnualScheduleDrawer({
  isOpen,
  context,
  scopeKey,
  selection,
  group: leagueGroup,
  week,
  onClose,
  onEmpty,
}: Props) {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { editAssignmentUrl } = useNavigationUrl();
  const { details, isDrawerOpen, isSuiteDrawerOpen, setHeadingElement, handleDrawerClosed } =
    useAnnualDrawerLifecycle({
      isOpen,
      scopeKey,
      selection,
      leagueGroup,
      week,
      onClose,
    });
  const { data, occurrences, loading, error, refresh } = usePackageAnnualCell(
    context,
    details?.selection,
    details?.week,
    scopeKey,
    isDrawerOpen,
  );
  const translationPrefix = "dimension.package.annual";
  const weekDateRange = details ? formatAnnualWeekDateRange(details.week) : "";
  const occurrencesByDate = useMemo(() => groupOccurrencesByDate(occurrences), [occurrences]);
  const reportedEmptyDataRef = useRef(data);
  const visibleHeaderPixels = useLayoutStore(state => state.visibleHeaderPixels);

  useEffect(() => {
    if (
      !isDrawerOpen ||
      loading ||
      error ||
      !data ||
      occurrences.length ||
      reportedEmptyDataRef.current === data
    )
      return;

    // Each empty detail response invalidates the summary once, without a refresh loop.
    reportedEmptyDataRef.current = data;
    onEmpty();
  }, [isDrawerOpen, loading, error, data, occurrences.length, onEmpty]);

  const handleEdit = () => {
    if (!isDrawerOpen || !details) return;
    onClose();
    history.push(editAssignmentUrl(details.leagueGroup.id));
  };

  const renderDetailContent = () => {
    if (!details) return null;

    if (loading) {
      return Array.from({ length: 3 }).map((_, index) => (
        <div
          key={index}
          role="status"
          aria-label={t(`${translationPrefix}.loading-detail`)}
          className={styles.annualScheduleDrawer_day}
        >
          <SkeletonBase height={14} width={240} />
          <SkeletonBase height={114} />
        </div>
      ));
    }

    if (error) {
      return (
        <div role="alert">
          <Text>{t(`${translationPrefix}.detail-error`)}</Text>
          <Button variant="ghost" onClick={() => void refresh()}>
            {t(`${translationPrefix}.retry`)}
          </Button>
        </div>
      );
    }

    if (!occurrencesByDate.length) return <Text>{t(`${translationPrefix}.empty-detail`)}</Text>;

    return occurrencesByDate.map(([date, dailyOccurrences]) => (
      <section key={date} className={styles.annualScheduleDrawer_day}>
        <Heading type="h3">{formatOccurrenceDate(date, i18n.language)}</Heading>
        {dailyOccurrences.map(occurrence => (
          <SessionOccurrenceDetails key={occurrence.id} occurrence={occurrence} context={context} />
        ))}
      </section>
    ));
  };

  const drawerHeader = details ? (
    <div ref={setHeadingElement} className={styles.annualScheduleDrawer_heading}>
      <Text type="md" className={styles.annualScheduleDrawer_week}>
        {details.week.name}
      </Text>
      <Heading type="h3" className={styles.annualScheduleDrawer_course}>
        {details.leagueGroup.course?.code} {details.leagueGroup.course?.name}
      </Heading>
      <div className={styles.annualScheduleDrawer_headingMeta}>
        {weekDateRange && (
          <>
            <Text type="sm" className={styles.annualScheduleDrawer_dates}>
              {weekDateRange}
            </Text>
            <span className={styles.annualScheduleDrawer_separator} aria-hidden="true">
              •
            </span>
          </>
        )}
        <Pill outlined>
          {t(`${translationPrefix}.league`)}: {details.leagueGroup.code}
        </Pill>
      </div>
    </div>
  ) : null;

  const drawerFooter = details ? (
    <div className={styles.annualScheduleDrawer_actions}>
      <Button variant="ghost" onClick={onClose} disabled={!isDrawerOpen}>
        {t(`${translationPrefix}.close`)}
      </Button>
      <Button onClick={handleEdit} disabled={!isDrawerOpen}>
        {t(`${translationPrefix}.edit`)}
      </Button>
    </div>
  ) : null;

  return (
    <Drawer
      isOpen={isSuiteDrawerOpen}
      closeOnOverlayClick
      onClose={handleDrawerClosed}
      className={styles.annualScheduleDrawer}
      contentClassName={styles.annualScheduleDrawer_content}
      title={drawerHeader}
      footer={drawerFooter}
      heightGap={visibleHeaderPixels}
    >
      <div className={styles.annualScheduleDrawer_body} aria-busy={loading}>
        {renderDetailContent()}
      </div>
    </Drawer>
  );
}
